import * as React from 'react';
import { Box, Container, Header, SpaceBetween } from '@amzn/awsui-components-console';
import { SnowAttribute } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { Store } from 'src/stores/store.interface';
import { useStore } from 'src/stores/store';
import TaskInputs from 'src/components/commonComponents/taskInputs/taskInputs';
import {
	buildSubWorkflowInputGroups,
	buildSubWorkflowTaskInputsFromChildTasks,
} from '../createTaskDefinitionUtils';

/**
 * Inputs of an embedded sub-workflow. The child's workflow-level inputs are laid out
 * flat, the way an ordinary task's inputs are, and its task-specific inputs follow in
 * one container per child task that owns them.
 */
export default function SubWorkflowTaskInputs() {
	const subWorkflowTaskInputs = useStore(
		(state: Store) => state.activeTaskDefinition.taskInputs
	);
	const childWorkflowId = useStore(
		(state: Store) => state.activeTaskDefinition.childWorkflowId
	);
	const savedSubWorkflowNodes = useStore(
		(state: Store) => state.WFDefinition.subWorkflows
	);
	const cachedChildWorkflowDefinition = useStore((state: Store) =>
		childWorkflowId ? state.childWorkflowDefinitions[childWorkflowId] : undefined
	);
	const setTaskInputs = useStore((state: Store) => state.setTaskInputs);
	const isFirstSubmissionDone = useStore(
		(state: Store) => state.isFirstSubmissionDone
	);

	const { displayedInputs, workflowLevelInputs, childTaskGroups } =
		React.useMemo(() => {
			const childTaskDefinitions =
				savedSubWorkflowNodes?.find(
					(subWorkflowNode) =>
						subWorkflowNode.workflowDefinition?.workflowId === childWorkflowId
				)?.taskDefinitions ?? cachedChildWorkflowDefinition?.taskDefinitions;

			const inputsFromChild =
				buildSubWorkflowTaskInputsFromChildTasks(childTaskDefinitions);
			const savedInputsByAttributeName = new Map(
				(subWorkflowTaskInputs ?? []).map((taskInput) => [
					taskInput.attributeName,
					taskInput,
				])
			);
			const editableInputs = inputsFromChild.length
				? inputsFromChild.map((taskInput) => {
					const savedInput = savedInputsByAttributeName.get(
						taskInput.attributeName
					);

					return savedInput
						? { ...taskInput, value: savedInput.value }
						: taskInput;
				})
				: subWorkflowTaskInputs ?? [];

			return {
				displayedInputs: editableInputs,
				...buildSubWorkflowInputGroups(editableInputs, childTaskDefinitions),
			};
		}, [
			subWorkflowTaskInputs,
			savedSubWorkflowNodes,
			childWorkflowId,
			cachedChildWorkflowDefinition,
		]);

	const updateTaskInputsForGroup = (updatedGroupInputs: SnowAttribute[]) => {
		const updatedInputsByAttributeName = new Map(
			updatedGroupInputs.map((updatedInput) => [
				updatedInput.attributeName,
				updatedInput,
			])
		);

		setTaskInputs(
			displayedInputs.map(
				(existingInput) =>
					updatedInputsByAttributeName.get(existingInput.attributeName) ??
					existingInput
			)
		);
	};

	if (!displayedInputs.length) {
		return (
			<Box textAlign="center" color="text-body-secondary" padding="m">
				This sub-workflow has no inputs for the parent workflow to supply
			</Box>
		);
	}

	return (
		<div data-testid="subWorkflowTaskInputs">
			<SpaceBetween size="m">
				{Boolean(workflowLevelInputs.length) && (
					<TaskInputs
						taskInputs={workflowLevelInputs}
						setTaskInputs={updateTaskInputsForGroup}
						isFirstSubmissionDone={isFirstSubmissionDone}
						isSubWorkflowTask
					/>
				)}
				{childTaskGroups.map((childTaskGroup) => (
					<Container
						key={childTaskGroup.childTaskId}
						header={
							<Header variant="h3">
								{childTaskGroup.childTaskName} Task Inputs
							</Header>
						}
					>
						<TaskInputs
							taskInputs={childTaskGroup.taskInputs}
							setTaskInputs={updateTaskInputsForGroup}
							isFirstSubmissionDone={isFirstSubmissionDone}
							isSubWorkflowTask
						/>
					</Container>
				))}
			</SpaceBetween>
		</div>
	);
}
